import React, { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import GlobalToast from './global-toast.coffee'
import {UIRouter, UIView, hashLocationPlugin} from '@uirouter/react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import theme from './theme.coffee'
import moment from 'moment'
import Application from './loopback_api.coffee'
import ErrorBoundary from './error_boundary.coffee'
import {installTransitionRecovery, retryAbortedRead} from './transition_recovery.coffee'
import {decodeRedirectParams, routeHash} from './deep_link.coffee'
import {patchPlaybackResourceHandoff} from './sound_playback_patch.coffee'
import { LoadingScreen as Loading, hide_agent_widget } from './common.coffee'
import axios from 'axios'
import ClientAPI from  "../../../../schema/framework/shared/client_api.coffee"
import {i18nCreateElement, initI18n, TranslationProvider, I18nRegistry, t} from '../../../../schema/framework/shared/i18n/index.coffee'
import {ENROLL_COMPLETE_STATE, ENROLL_COMPLETE_PATH} from './StudentApplication/enrollRedirect.coffee'
import {subscribedWithoutStudents, STUDENTS_SCREEN_HASH} from './subscriptionRouting.coffee'
import * as remoteLogging from './remote_logging.coffee'

# createjs 1.0.0 hands a freshly loaded buffer to the instances waiting for it
# with a method that release does not have, and throws instead (card
# 6aab65a36c29c3a6dc3bbbb7). Patched HERE, at module scope in the webpack entry,
# because AbstractPlugin.register captures the handler as the loader's complete
# listener — a sound registered before this call would keep the broken one.
patchPlaybackResourceHandoff()

# Normalize route by removing student IDs but keeping game IDs
# This is el-feet specific routing logic
normalizeRoute = (route) ->
  return route unless route?
  # Remove studentId from /play/:gameId/:studentId routes
  # Keep the gameId since each game has different strings
  route.replace(/^(#?\/play\/[0-9a-fA-F]{24})\/[0-9a-fA-F]{24}/, '$1')

# on old iOS, the bridge sometimes takes an unnecessarily long time to load, so
# this listens for the load event, then updates the components if it is received
AppListener = (WrappedComponent) ->
  class extends React.Component
    isAppChanged: () =>
      if window.app.isApp()
        hide_agent_widget()

      @forceUpdate()

    componentDidMount: () ->
      document.addEventListener('el-is-app-changed', @isAppChanged)

    componentWillUnmount: () ->
      document.removeEventListener('el-is-app-changed', @isAppChanged)

    render: () ->
      <WrappedComponent {...@props} />

# components all have a loading screen, and listen for isApp changes
standardComponent = (importPromise) ->
  LazyComponent = lazy(() => importPromise)
  AppListener((props) ->
    <Suspense fallback={<Loading/>}>
      <LazyComponent {...props} />
    </Suspense>
  )

# all route components with loading screens and such
AffiliateSetup     = standardComponent import('./Affiliate/setup.coffee')
Assignment         = standardComponent import('./Assignment/assignment.coffee')
Calendar           = standardComponent import('./Calendar/calendar.coffee')
Help               = standardComponent import('./Help/help.coffee')
SubscriptionExpired = standardComponent import('./SubscriptionExpired/subscription_expired.coffee')
Login              = standardComponent import('./Login/login.coffee')
Dashboard          = standardComponent import('./Dashboard/dashboard.coffee')
ManageAccount      = standardComponent import('./ManageAccount/account.coffee')
MilestoneReport    = standardComponent import('./MilestoneReport/milestone_report.coffee')
LongMilestoneReport    = standardComponent import('./MilestoneReport/long_milestone_report.coffee')
GameLayout         = standardComponent import('./Game/game_layout.coffee')
StudentReport      = standardComponent import('./StudentReport/student_report.coffee')
StudentApplication = standardComponent import('./StudentApplication/application.coffee')
EnrollComplete     = standardComponent import('./StudentApplication/enroll_complete.coffee')
UpdateScholarship  = standardComponent import('./UpdateScholarship/update_scholarship.coffee')
GamesList          = standardComponent import('./GameList/games.coffee')
SubjectList       = standardComponent import('./SubjectList/subjects.coffee')
TeacherUpgrade     = standardComponent import('./TeacherUpgrade/teacher_upgrade.coffee')
Tokens             = standardComponent import('./Tokens/tokens.coffee')
HistoryPlayer      = standardComponent import('./GamePlayback/player.coffee')
CancellationSurvey = standardComponent import('./CancellationSurvey/cancellation_survey.coffee')
Cancel             = standardComponent import('./CancellationSurvey/new_cancellation_survey.coffee')
Worksheet          = standardComponent import('./Dashboard/worksheet.coffee')
CoachingVideos     = standardComponent import('./CoachingVideos/videos.coffee')
requireLogin = (trans, state) ->
  unless window.app?.loopbackAPI?.isLoggedIn()
    original_target = trans.targetState().name()
    original_params = trans.targetState().params()
    console.log "Redirecting logged-out user to the login page. Original target: #{original_target}, Original Params:"
    console.dir original_params
    return { state: 'login', params: { original_target, original_params } }

# Manage Account and QR Logins are the parent's screens. The drawer hides them
# from a student-locked (QR) session (title_bar.coffee), but a typed or
# bookmarked #/account still rendered the whole page, so the route refuses
# them too and sends the student to their dashboard (card
# 6aa4d3ed1c031a8daf2e49c3).
requireParent = (trans, state) ->
  loginRedirect = requireLogin(trans, state)
  return loginRedirect if loginRedirect?
  if window.app?.isStudentLock?()
    console.log "Student-locked session: #{trans.targetState().name()} is a parent screen, redirecting to the dashboard"
    return { state: 'dashboard' }

globalStates = [
  name: 'login',
  url: '',
  params: {
    original_target: { type: 'string', value: null }
    original_params: { type: 'any', value: null }
  },
  component: Login,
  redirectTo: (trans) ->
    if window.app?.loopbackAPI?.isLoggedIn()
      if window.app.login.account?.status == 'Trial'
        console.log "Redirecting logged-in user to the application..."
        return { state: 'application' }
      else
        console.log "Redirecting logged-in user to the dashboard..."
        return { state: 'dashboard' }
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Login")
  ]
,
  # #/logout reached WITHOUT a page load — a hash link, a typed hash, a goto
  # from inside the app — was never a state: the URL fell through to
  # `otherwise` and a Trial account was routed back to #/application still
  # signed in (card 6aa9ab4ba13a32e41e79f8a6). Signing out here makes it the
  # same logout the page-load branch performs.
  name: 'logout',
  url: '/logout',
  redirectTo: ->
    window.app.loopbackAPI.logout() if window.app?.loopbackAPI?.isLoggedIn()
    { state: 'login' }
,
  name: 'affiliate_setup',
  url:'/affiliate_setup',
  redirectTo: requireLogin
  component: AffiliateSetup,
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Affiliate Setup")
  ]
,
  name: 'application',
  url:'/application',
  redirectTo: requireLogin
  component: StudentApplication,
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Application")
      window.app.trackLead()
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  # Where Stripe sends the browser back after a redirect-based payment method
  # during sign-up (card 6a9cb1d0ea6ab4f8aea9c463). Stripe's parameters ride
  # on the QUERY string, not on this hash route, so the url carries none.
  name: ENROLL_COMPLETE_STATE,
  url: ENROLL_COMPLETE_PATH,
  redirectTo: requireLogin
  component: EnrollComplete,
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Enrollment Complete")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'account'
  url:'/account?plan&tab'
  redirectTo: requireParent
  component: ManageAccount
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Account")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'videos'
  url:'/videos'
  redirectTo: requireLogin
  component: CoachingVideos
  resolve:[
    token: "videos",
    resolveFn: ->
      window.app.trackPageView("2: Coaching Videos")
      window.app.loopbackAPI.Video.find({order: "position ASC"})
  ]
,
  name: 'assignment'
  url:'/assignment'
  redirectTo: requireLogin
  component: Assignment
  resolve:[
    token: "assignments",
    resolveFn: ->
      window.app.trackPageView("2: Assignment")
      window.app.loopbackAPI.Assignment.find({})
  ]
,
  name: 'calendar'
  url:'/calendar'
  redirectTo: requireLogin
  component: Calendar
  resolve:[
    token: "none",
    resolveFn: ->
      window.app.trackPageView("2: Calendar")
  ]
,
  name: 'dashboard'
  url: '/dashboard?studentId'
  # dynamic: the dashboard writes the selected child here as the parent swipes
  # (card 6aa8b5d750645cd1c3e765ab) without remounting itself.
  params:
    studentId:
      dynamic: true
  redirectTo: requireLogin
  component: Dashboard
  resolve:[
    token: "initialized",
    resolveFn: ->
      window.app.trackPageView("2: Dashboard")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'games'
  url:'/games?studentId'
  redirectTo: requireLogin
  component: GamesList
  resolve:[
    token: 'games'
    deps: ['$stateParams']
    resolveFn: ({studentId}) ->
      window.app.trackPageView("2: Game List")
      # studentId comes from $stateParams, NOT from the active student: this
      # resolve runs BEFORE digestParams latches it, so reading the active
      # student here filters the game voices for the child being navigated
      # away from (card 6aa4313530706c48305d2195).
      window.app.loadGames(studentId)
  ,
    token: 'refresh'
    resolveFn: ->
      if window.app.needsRefresh()
        student = window.app.getActiveStudent()
        session_duration = if student.session_duration? then student.session_duration else null
        p = window.app.refresh().then ->
          window.app.getActiveStudent().session_duration = session_duration if session_duration?
        return p
  ]
,
  name: 'subjects'
  url:'/subjects?studentId'
  redirectTo: requireLogin
  component: SubjectList
  resolve:[
    token: 'subjects'
    resolveFn: ->
      window.app.trackPageView("2: Subject List")
  ,
    token: 'refresh'
    resolveFn: ->
      if window.app.needsRefresh()
        student = window.app.getActiveStudent()
        session_duration = if student.session_duration? then student.session_duration else null
        p = window.app.refresh().then ->
          window.app.getActiveStudent().session_duration = session_duration if session_duration?
        return p
  ]
,
  name: 'play'
  url:'/play/:gameId/:studentId?nodeId&activityId&successAnimationId&closeOnComplete'
  redirectTo: requireLogin
  component: GameLayout
  resolve:[
    token: 'studentRefresh',
    resolveFn: ({studentId}) ->
      console.log "Refreshing student #{studentId}..."
      response = await window.app.loopbackAPI.PosetProfile.find({where: {studentId: studentId}, include: {relation: 'poset', scope: {fields: ['name']}}})
      student = window.app.getStudentById(studentId)
      if student
        student.posetProfiles = response.data
        window.app.dirty = true
    deps: ['$stateParams']
  ,
    token: 'games'
    deps: ['$stateParams']
    resolveFn: ({studentId}) ->
      window.app.trackPageView("2: Game Play")
      # studentId comes from $stateParams, NOT from the active student: this
      # resolve runs BEFORE digestParams latches it, so reading the active
      # student here filters the game voices for the child being navigated
      # away from (card 6aa4313530706c48305d2195).
      window.app.loadGames(studentId)
  ]
,
  name: 'playSubject'
  url:'/playsubject/:subjectId/:studentId?nodeId&activityId&successAnimationId&closeOnComplete'
  redirectTo: requireLogin
  component: GameLayout
  resolve:[
    token: 'studentRefresh',
    resolveFn: ({studentId}) ->
      console.log "Refreshing student #{studentId}..."
      response = await window.app.loopbackAPI.PosetProfile.find({where: {studentId: studentId}, include: {relation: 'poset', scope: {fields: ['name']}}})
      student = window.app.getStudentById(studentId)
      if student
        student.posetProfiles = response.data
        window.app.dirty = true
    deps: ['$stateParams']
  ,
    token: 'games'
    deps: ['$stateParams']
    resolveFn: ({studentId}) ->
      window.app.trackPageView("2: Game Play")
      # studentId comes from $stateParams, NOT from the active student: this
      # resolve runs BEFORE digestParams latches it, so reading the active
      # student here filters the game voices for the child being navigated
      # away from (card 6aa4313530706c48305d2195).
      window.app.loadGames(studentId)
  ]
,
  name: 'playback'
  url: '/playback/:history_id'
  redirectTo: requireLogin
  component: HistoryPlayer
  resolve:[
    token: 'history_item'
    deps: ['$transition$']
    resolveFn: (trans) =>
      window.app.trackPageView("2: Playback")
      lbapi = window.app.loopbackAPI
      return lbapi.History.findById({id: trans.params().history_id}, {include: [{game: 'gameTheme'}, 'student', 'poset', 'milestone', 'node']})
  ,
     token: 'recording'
     deps: ['$transition$']
     resolveFn: (trans) =>
       return window.app.fetchHistory(trans.params().history_id)
  ]
,
  name: 'student_report'
  url: '/student_report?studentId'
  redirectTo: requireLogin
  component: StudentReport
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Student Report")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'milestone_report'
  url: '/milestone_report?studentId'
  redirectTo: requireLogin
  component: MilestoneReport
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Placement Exam Report")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'long_milestone_report'
  url: '/long_milestone_report?studentId'
  redirectTo: requireLogin
  component: LongMilestoneReport
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Long Placement Exam Report")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'teacher_upgrade'
  url: '/teacher_upgrade'
  redirectTo: requireLogin
  component: TeacherUpgrade
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Teacher Upgrade")
  ]
,
  name: 'tokens'
  url:'/tokens'
  redirectTo: requireParent
  component: Tokens
  resolve:[
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Login Tokens")
  ]
,
  name: 'update_scholarship'
  url: '/update_scholarship?back'
  redirectTo: requireLogin
  component: UpdateScholarship
  resolve: [
    token: 'back'
    deps: ['$transition$']
    resolveFn: (trans) =>
      trans.params().back
  ,
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Update Scholarship")
  ]
,
  name: 'cancellation_survey'
  url: '/cancellation_survey'
  redirectTo: requireLogin
  component: CancellationSurvey
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Cancellation Survey")
  ]
,
  name: 'cancel'
  url: '/cancel'
  redirectTo: requireLogin
  component: Cancel
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Cancel")
  ]
,
  name: 'help'
  url: '/help'
  params: {url: {type: 'string', value: null}}
  redirectTo: requireLogin
  component: Help
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Help")
  ]
,
  # A student-locked session whose family's subscription has ended (card
  # 6aa6cd1fcbca3e2eac7b42ef). Reads nothing, so it renders on the
  # Student.unsubscribed manifest the dashboard cannot.
  name: 'subscription_expired'
  url: '/subscription_expired'
  redirectTo: requireLogin
  component: SubscriptionExpired
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Subscription Expired")
  ]
,
  name: 'worksheet'
  url: '/worksheet?studentId'
  redirectTo: requireLogin
  component: Worksheet
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Worksheet")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
]

# The query can sit before the fragment (/org?access_token=..&uid=..#/dashboard)
# or inside it (/app#/login?access_token=.., GET /login's redirect), so cut the
# fragment off what follows the last '?', never off the URL: a uid that
# swallowed '#/dashboard' installed a session for no login (card 6aa6dbae617b8dc32cc6b4c0).
parseUrl = ( url = location.href ) ->
  params = {}
  ( ( parts = part.split( "=" ) ) && params[ parts[0] ] = parts[1] for part in ( url.split "?" ).pop().split( "#" )[0].split "&" if url.indexOf( "?" ) != -1 ) && params || {}

params = parseUrl window.location.href
console.log "View Params:"
console.dir params
clientAPI = new ClientAPI(window.config.basePath || '', "/clientapi", "$LoopBack$", {
  socketExcludePatterns: ['placement_report']
  blobOperations: ['Student.placement_report']
})
# Pre-set auth from URL params before loading schema.
# Without this, arriving with no cached token causes loadSchema() to run
# unauthenticated, returning a schema missing subscriber-only methods.
if params.access_token? && !/#/.test(params.studentId)
  if !clientAPI.isLoggedIn() ||
     params.new_user == 'true' ||
     (params.studentId? && params.studentId != clientAPI.currentStudentId)
    clientAPI.setUser(params.access_token, params.uid, undefined, params.studentId)
    clientAPI.resetAuthHeader()
clientAPI.loadSchema().then () ->
  window.app = new Application(clientAPI)

  # Remote Logging survives a reload on purpose (card 6aac4153fb9b132bb83a8911).
  # A customer arms it, then reproduces the problem — and reproducing almost
  # always involves navigating, so an arm that died at the first page load
  # would capture everything except the thing support asked for. Never allowed
  # to fail the boot: a diagnostic that takes the app down is worse than one
  # that does not start.
  try
    remoteLogging.restore()
  catch err
    console.warn "[remote logging] could not restore an armed session: #{err?.message or err}"

  # Make i18n functions globally available
  window.i18nCreateElement = i18nCreateElement
  window.t = t

  # TEMPORARY HACK: we have to load the css manually until the next version is
  #               : deployed to Android
  if window.app.isAndroid()
    console.warn "Android :: Checking for old *fake* CSS..."
    oldNode = document.querySelector('link[href="https://unpkg.com/react-md@1.2.3/dist/react-md.purple-deep_orange.min.css"]')
    if oldNode?
      console.warn "Android :: Updating old *fake* CSS..."
      oldNode.setAttribute('href', "#{window.config.s3_assets}css/react-md.min.css")

      console.warn "Android :: Adding new resets CSS..."
      resetsNode = document.createElement('link')
      resetsNode.setAttribute('rel', 'stylesheet')
      resetsNode.setAttribute('href', "#{window.config.s3_assets}css/resets.css")

      parentNode = oldNode.parentNode
      parentNode.insertBefore(resetsNode, oldNode)

  hide_agent_widget() if window.app.isApp()

  configure = (router) ->
    router.urlRouter.otherwise {state: 'dashboard'}
    # A blank grey screen after an aborted XHR is not an outcome anyone can
    # act on (card 6aab65a36c29c3a6dc3bbbb5).
    installTransitionRecovery router


    # Update i18n context on route transitions
    router.transitionService.onSuccess {}, (transition) ->
      toState = transition.to()
      # Strip query parameters from route for cleaner tracking
      routeWithoutQuery = window.location.hash.split('?')[0]
      normalizedRoute = normalizeRoute(routeWithoutQuery)
      window.i18nRegistry?.setContext(
        "elfeet/#{toState.name}",
        normalizedRoute,
        'elfeet'
      )

  start_react = ->
    root = createRoot(document.getElementById("root"))
    root.render(
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <TranslationProvider route={window.location.hash} app="elfeet">
            <UIRouter plugins={[hashLocationPlugin]} states={globalStates} config={configure}>
              <div style={{height: "100%"}}>
                <GlobalToast/>
                <UIView/>
              </div>
            </UIRouter>
          </TranslationProvider>
        </ThemeProvider>
      </ErrorBoundary>
    )

  class GetAccountError extends React.Component
    componentDidMount: () ->
      window.app.ready()
      throw @props.error

    render: () ->
      <div></div>

  render_react_error = (error) ->
    root = createRoot(document.getElementById("root"))
    root.render(
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <GetAccountError error={error} />
        </ThemeProvider>
      </ErrorBoundary>
    )

  initialize = (route_to='dashboard', route_params={}) ->
    window.application_running = 'parent'
    # ONE RETRY ON AN ABORTED BOOT READ — card 6aab772ced72ee7fceb166f3. This
    # read runs before the router exists, so transition_recovery's onError hook
    # is not installed yet and cannot catch it; without this, a single transient
    # abort put a child on the "We have encountered an error" page with no way
    # out but a manual reload.
    retryAbortedRead((-> window.app.refresh()), (error) ->
      console.warn "App boot read was aborted (#{error?.message ? error}) — retrying once"
    ).then (response) ->
      if response.data?.login? # we did not fetch account data, send them to login screen...
        # login was successful, clear the QR login flag
        window.app.clearQRFlag()

        # Initialize i18n with user's preferred language
        # Check both snake_case (from cache) and camelCase (from DB) field names
        # For student sessions, use the student's preferred_language
        student0 = response.data.login.account.students?[0]
        preferredLang = response.data.login.preferredLanguage || response.data.login.preferred_language || student0?.preferred_language || 'en'
        # console.log "Initializing i18n with preferred language: #{preferredLang}"
        initI18n(clientAPI, preferredLang)

        # Initialize i18n registry
        window.i18nRegistry = new I18nRegistry(clientAPI)
        # Strip query parameters from route for cleaner tracking
        routeWithoutQuery = window.location.hash.split('?')[0]
        normalizedRoute = normalizeRoute(routeWithoutQuery)
        window.i18nRegistry.setContext('elfeet/init', normalizedRoute, 'elfeet')

        # Always fetch all translations from server for non-English locales
        # This ensures newly imported strings are loaded, even if localStorage cache exists
        # 'en-US' is not 'en': ask the framework whether this locale reads the
        # authored text before fetching the catalogue (card 6aa6c73847c945dd8596e7d9).
        if !window.i18nManager.readsSource()
          # console.log "⏳ Fetching all translations for #{preferredLang}..."
          await clientAPI.i18nString.find({where: {app: 'elfeet'}}).then (resp) ->
            cache = {}
            strings = if Array.isArray(resp) then resp else (resp?.data || [])
            for string in strings
              cache[string.hash] = string.text
            try
              # Save to localStorage
              localStorage.setItem("i18n_#{preferredLang}_elfeet", JSON.stringify(cache))
              # Merge with any existing cache (from upsertBatch responses)
              window.i18nTranslationCache ?= {}
              for hash, text of cache
                window.i18nTranslationCache[hash] = text
              # console.log "✅ Loaded #{Object.keys(cache).length} translations from server"
            catch e
              console.warn "Failed to cache:", e

        account_type = window.app.account.type
        #not expired
        if !window.app.isStudentLock() #if there is a student id it could be a student login
          if account_type == 'teacher' && !window.app.isMobile()
            token = window.app.loopbackAPI.accessTokenId
            userId = window.app.loopbackAPI.currentUserId
            return window.location = "/teacher?access_token=#{token}&uid=#{userId}"
          else if account_type in ['organization', 'district', 'school']
            token = window.app.loopbackAPI.accessTokenId
            userId = window.app.loopbackAPI.currentUserId
            role = window.app.loopbackAPI.currentUserRole
            if role == "Login.district"
              theurl = "/org?access_token=#{token}&uid=#{userId}&masquerade=district"
            else
              theurl = "/org?access_token=#{token}&uid=#{userId}"
            if window.app.isAndroid()
              return window.location = window.config.server+theurl
            else
              return window.location = theurl
            
        if route_to == ENROLL_COMPLETE_PATH.substring(1)
          # Back from a redirect-based payment method with the SetupIntent
          # confirmed and NOT YET subscribed — which is exactly the account
          # shape every branch below sends to #/application, restarting the
          # form. The landing state does the subscribe; let it
          # (card 6a9cb1d0ea6ab4f8aea9c463). Assigning a hash keeps the
          # query string Stripe appended.
          #
          # Deliberately NOT routeHash: this branch is the Stripe return, whose
          # parameters are in the URL's own query string, not a deep link's
          # redirectParams (card 6aab72ceed72ee7fceb166c3).
          window.location = "#/#{route_to}"
        else if response.data.login.account.status is 'Subscribed' && moment(response.data.login.account.subscription_end).isAfter(moment()) && window.app.students.length > 0
          # if the user still needs to complete the onboarding process, send them there
          if window.app.account.force_onboarding && route_to != 'application'
            window.location = "#/assignment"
          else
            window.location = routeHash route_to, route_params
        else if window.app.isUnsubscribedTeacher()
          window.location.hash = routeHash route_to, route_params
        else if subscribedWithoutStudents(response.data.login.account, window.app.students, window.app.sharedStudents)
          # A paying account with every student removed. The checkout below is
          # for accounts with nothing to pay with; this one has a live
          # subscription and nobody to teach, and its role (Login.subscribed)
          # holds no Account.subscribe grant, so the form would end in a raw
          # 401. Owner decision 2026-09-06, card 6a9cc47fdbd420ed5794d248: the
          # Students screen, not #/application.
          route_to = 'account'
          window.location = STUDENTS_SCREEN_HASH
        else if (window.app.students.length == 0 && window.app.sharedStudents.length == 0) || response.data.login.account.status is 'Trial'
          # route_to is REPLACED here, so the link's own params do not travel
          # with it — they described the screen being diverted away from
          # (card 6aab72ceed72ee7fceb166c3).
          route_to = 'application'
          window.location = "#/#{route_to}"
        else if moment(response.data.login.account.subscription_end).isBefore(moment())
          if route_to in ["update_scholarship"]
            window.location = routeHash route_to, route_params
          else if window.app.isStudentLock()
            # A student session carries no billing fields to route on and has
            # no business on the parent's screens (card 6aa4d3ed1c031a8daf2e49c3).
            # Not the dashboard either: Student.unsubscribed's manifest has no
            # History or PosetProfile, so it crashed to the error screen (card
            # 6aa6cd1fcbca3e2eac7b42ef). A screen that reads nothing.
            window.location = '#/subscription_expired'
          else if response?.data?.login?.account?.customer?.id? || response?.data?.login?.account?.organization? || response?.data?.login?.account?.transaction?
            window.location = "#/account"
          else
            window.location = '#/application'
        else
          window.location.hash = ''

        # WEBFLOW :: save the initial routing to a variable for webflow
        window.app.setInitialRouting(route_to)

        start_react()
      else
        window.location.hash = ''
    .catch (error) ->
      render_react_error(error)

  window.initialize = initialize
  if params.code? && window.app.loopbackAPI?
      console.log "Send Code to server to check login"
      window.app.loopbackAPI.clearUser()
      window.app.loopbackAPI.Login.cleverCode({code: params.code}).then (response) ->
        console.log "Got response from server:"
        console.dir response
        if response.data?.success == false
          window.app.login_error_message = response.data?.message
          return start_react()
        else if response.data?.data?
          token_info = response.data.data
          window.app.loopbackAPI.setUser(token_info.access_token, token_info.user_id, undefined, token_info.student_id)
          window.app.loopbackAPI.resetAuthHeader()
        window.history.replaceState {}, document.title, "/app"
        initialize()
  else if window.app.loopbackAPI.isLoggedIn()
    # reset login if user is a new user (fast_create) or a different student
    if (params.access_token && params.new_user is 'true') ||
       (params.studentId? && params.access_token? && params.studentId != window.app.loopbackAPI.currentStudentId && !/#/.test(params.studentId))
      window.app.loopbackAPI.setUser(params.access_token, params.uid, undefined, params.studentId)
      window.app.loopbackAPI.resetAuthHeader()
      window.history.replaceState {}, document.title, "/app"
      initialize params.redirectTo, decodeRedirectParams(params.redirectParams)
    else if params.access_token? && params.redirectTo?
      # A SERVER-MINTED DEEP LINK ON AN ALREADY-INSTALLED SESSION — card
      # 6aab72ceed72ee7fceb166c3, found by driving the real link on the seat.
      #
      # The `access_token?` branch further down is where redirectTo used to be
      # read, and it is UNREACHABLE for a link that carries a token: the
      # module-scope pre-set above (`if params.access_token? …
      # clientAPI.setUser`) installs the session before this runs, so
      # isLoggedIn() is already true and control never gets there. Without this
      # branch the link fell to the empty-hash case below and opened the
      # dashboard — the redirect discarded in silence, which is exactly the
      # symptom the card describes.
      #
      # Still gated on access_token, per the owner's ruling 2026-09-17: a
      # redirect is honoured only where the server minted the link.
      window.history.replaceState {}, document.title, "/app"
      initialize params.redirectTo, decodeRedirectParams(params.redirectParams)
    else if !window.location.hash? || window.location.hash is '' || window.location.hash is '#/'
      initialize()
    else if window.location.hash is '#/logout'
      window.app.loopbackAPI.logout()
      # Render, or a page loaded at #/logout signs out onto a blank page; the
      # router's logout state lands on the login screen.
      start_react()
    else
      route = window.location.hash.substring 2
      initialize route
  else if params.access_token? && !/#/.test(params.studentId)
    window.app.loopbackAPI.setUser(params.access_token, params.uid, undefined, params.studentId)
    window.app.loopbackAPI.resetAuthHeader()
    window.history.replaceState {}, document.title, "/app"
    initialize params.redirectTo, decodeRedirectParams(params.redirectParams)
  else
    start_react()

